import {
    ChatContainer, MainContainer, Message, MessageInput,
    MessageList, TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import {
    Button,
    Card, Image, Space, Spin, Tooltip,
} from 'antd';
import axios from 'axios';
import { produce } from 'immer';
import moment from 'moment';
import {
    ChangeEventHandler, useEffect, useRef, useState,
} from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { v4 } from 'uuid';
import { CloseOutlined, DownloadOutlined, FileDoneOutlined } from '@ant-design/icons';
import { useApiFetch } from '../../hooks/api/useApiFetch';
import { useUser } from '../../hooks/useUser';

interface IAgentData {
    id: string;
    name: string;
    meta?: {
        knowledge: {
            id: string;
            type: string;
        }[];
        prompts?: string[];
    }
}

interface IMessage {
    content: string;
    role: 'assistant' | 'user' | 'system';
    timestamp?: number;
    files?: {
        content_type: string;
        id: string;
        name: string;
    }[];
}

interface IFile {
    id: string;
    user_id: string;
    hash: any;
    filename: string;
    data: Object;
    meta: {
        'name': string;
        'content_type': string;
        'size': number;
        'file_hash': string;
        'data': Object;
    }
    created_at: number;
    updated_at: number;
}

const AiAgentChatPage = () => {
    const api = useApiFetch();
    const { id } = useParams();
    const [chatId, setChatId] = useState<string|null>();
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [model, setModel] = useState<IAgentData>();
    const [selectedFiles, setSelectedFiles] = useState<IFile[]>([]);
    const [sending, setSending] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const ioSocket = useRef<Socket>();
    const lastMessageId = useRef<string>();
    const { info } = useUser(false);
    const [show, setShow] = useState(false);
    const [newMessage, setNewMessage] = useState<any>();

    const createChat = async () => {
        const response = await axios({
            url: 'https://ai.retechcrm.ru/api/v1/chats/new',
            method: 'POST',
            data: {
                model: model?.id,
                chat: {
                    title: info?.fio,
                },
            },
            withCredentials: true,
        });
        api.fetchData('/ai-chats/create', 'post', true, {
            agent_id: model?.id,
            chat_id: response.data.id,
        });
        setChatId(response.data.id);
    };

    const getMessages = async () => {
        if (typeof chatId === 'string') {
            try {
                const response = await axios.create().request({
                    url: `https://ai.retechcrm.ru/api/v1/chats/${chatId}`,
                    withCredentials: true,
                });
                if (response.data.chat.history instanceof Object) {
                    const x = Object.values(response.data.chat.history.messages).filter((el: any) => typeof el.content === 'string' && el.content.length > 0);
                    setMessages(x.map((obj: any) => ({
                        content: obj.content,
                        role: obj.role,
                        timestamp: obj.timestamp,
                        files: obj.files,
                    })));
                    lastMessageId.current = response.data.chat.history.currentId;
                }
            } catch (err) {
                await api.fetchData(`/ai-chats/delete/${model?.id}`, 'delete', true);
                createChat();
            }
        }
    };

    const getChat = async () => {
        const response = await api.fetchData<{ chat_id: string | null; }>(`/ai-chats/${model?.id}`, 'get', true);
        if (response.success) {
            if (typeof response.data?.chat_id === 'string') {
                setChatId(response.data?.chat_id);
            } else {
                createChat();
            }
        }
    };

    const getInfo = async () => {
        setLoading(true);
        const response = await axios({
            url: `https://ai.retechcrm.ru/api/v1/models/model?id=${id}`,
            withCredentials: true,
        });
        if (response.status === 200 && response.data instanceof Object) {
            setModel(response.data);
            // if (Array.isArray(response.data.meta.prompts)) {
            //     let promptsData = await Promise.all(response.data.meta.prompts.map(async (promptId: string) => {
            //         try {
            //             const res = await axios({
            //                 url: `https://ai.retechcrm.ru/api/v1/prompts/id/${promptId}`,
            //                 headers: {
            //                     Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            //                 },
            //             });
            //             if (res.status === 200) {
            //                 return res.data;
            //             }
            //             return undefined;
            //         } catch {
            //             return undefined;
            //         }
            //     }));
            //     promptsData = promptsData.filter((el) => el instanceof Object);
            //     setMessages(promptsData.map((el) => ({
            //         content: el.content,
            //         role: 'system',
            //     })));
            // }
        }
        setLoading(false);
    };

    const fileUpload = async (file: File): Promise<{ id: string } | null> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axios({
                url: 'https://ai.retechcrm.ru/api/v1/files/?process=false',
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                data: formData,
                method: 'POST',
                withCredentials: true,
            });
            if (response.status === 200 && response.data instanceof Object) {
                return response.data;
            }
            return null;
        } catch {
            return null;
        }
    };

    const onFileSelect: ChangeEventHandler<HTMLInputElement> = async (e) => {
        setUploading(true);
        if (e.target instanceof Object && e.target.files && e.target.files?.length > 0) {
            const uploadedFiles = (await Promise.all(Array.from(e.target.files).map(fileUpload))).filter((el) => typeof el?.id === 'string');
            setSelectedFiles([
                ...selectedFiles,
                ...uploadedFiles as any,
            ]);
        }
        setUploading(false);
    };

    const onFileDeleteClick = (file: IFile) => {
        const nextState = produce(selectedFiles, (draft) => {
            const index = draft.findIndex((el) => el.id === file.id);
            if (index > -1) {
                draft.splice(index, 1);
            }
            return draft;
        });
        setSelectedFiles(nextState);
    };

    useEffect(() => {
        // axios({
        //     url: 'https://ai.retechcrm.ru/api/v1/chats/',
        //     headers: {
        //         Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
        //     },
        // }).then((response) => {
        //     response.data.forEach((item: any) => {
        //         axios({
        //             url: `https://ai.retechcrm.ru/api/v1/chats/${item.id}`,
        //             headers: {
        //                 Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
        //             },
        //             method: 'delete',
        //         });
        //     });
        // });

        if (typeof ioSocket.current === 'undefined') {
            ioSocket.current = io('https://ai.retechcrm.ru', {
                autoConnect: true,
                path: '/ws/socket.io',
                transports: ['websocket'],
                auth: {
                    token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Ijc0NTUyOThhLTIxN2QtNGRlZC04OTNhLWI4ZjJiNGY5YTI0YyIsImV4cCI6MTc5MDQ0NjE3NSwianRpIjoiMzFkNDMxMDAtMTNhZi00ZDc5LWI2MTMtZTY0YmIxZjhmMDQ1IiwiaWF0IjoxNzg4MDI2OTc1fQ.QAkObh5JqNvt69uZcqEDIHmO-kmqkYBipkHo9Pf8pmg',
                },
            });

            ioSocket.current.on('connect', () => {
                ioSocket.current?.io.engine.on('data', (raw) => {
                    // eslint-disable-next-line no-useless-escape
                    const [type, event] = JSON.parse(raw.replace(/[^\[]*/, ''));
                    if (type === 'events' && event instanceof Object) {
                        if (event.data.type === 'chat:completion') {
                            setWaiting(true);
                            const { data } = event.data;
                            if (data instanceof Object && data.done) {
                                const message = data.output.find((el: any) => el.type === 'message');
                                if (message instanceof Object) {
                                    setNewMessage({
                                        content: message.content[0].text,
                                        role: 'assistant',
                                        timestamp: moment().unix(),
                                    });
                                    setWaiting(false);
                                }
                            }
                        }
                    }
                });
            });

            ioSocket.current.on('data', (data) => {
                console.log(data);
            });

            ioSocket.current.on('chat:completion', (data) => {
                console.log(data);
            });

            ioSocket.current.emit('join', { room: ioSocket.current.id });
        }

        axios({
            url: 'https://ai.retechcrm.ru/api/v1/auths/signin',
            method: 'post',
            data: {
                email: 'sasha.yahatin@yandex.ru',
                password: 'Krzxanbyc123',
            },
            withCredentials: true,
        }).then(() => {
            setShow(true);
        });
    }, []);

    useEffect(() => {
        if (show) {
            getInfo();
        }
    }, [id, show]);

    useEffect(() => {
        if (typeof model?.id === 'string') {
            getChat();
        }
    }, [model?.id]);

    useEffect(() => {
        getMessages();
    }, [chatId]);

    useEffect(() => {
        if (newMessage) {
            const nextState = produce(messages, (draft) => {
                draft.push(newMessage);
            });
            setMessages(nextState);
            setNewMessage(undefined);
        }
    }, [newMessage]);

    const onAttachClick = () => inputRef.current?.click();

    const onMessageSend = async (html: string) => {
        setSending(true);
        const messageId = v4();

        const response = await axios({
            url: 'https://ai.retechcrm.ru/api/chat/completions',
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'POST',
            withCredentials: true,
            data: {
                chat_id: chatId,
                id: messageId,
                stream: true,
                session_id: ioSocket.current?.id,
                model: model?.id,
                features: {
                    voice: false,
                    image_generation: false,
                    code_interpreter: false,
                    web_search: true,
                    memory: true,
                },
                user_message: {
                    id: v4(),
                    parentId: lastMessageId.current,
                    role: 'user',
                    content: html,
                    files: selectedFiles.map((file) => ({
                        type: 'file',
                        file,
                        id: file.id,
                        url: file.id,
                        name: file.filename,
                        status: 'uploaded',
                        size: file.meta.size,
                        error: '',
                        content_type: file.meta.content_type,
                    })),
                },
                background_tasks: {
                    follow_up_generation: false,
                },
            },
        });

        if (response.status === 200 && response.data instanceof Object) {
            lastMessageId.current = messageId;
            getMessages();
            setWaiting(true);
            setSelectedFiles([]);
        }

        setSending(false);
    };

    const renderMessage = (message: IMessage, index: number) => {
        const otherFiles = Array.isArray(message.files) && message.files.length > 0 ? message.files.filter((el) => !el.content_type.includes('image') && !el.content_type.includes('audio') && !el.content_type.includes('video')) : [];
        const images = Array.isArray(message.files) && message.files.length > 0 ? message.files.filter((el) => el.content_type.includes('image')) : [];
        const audioFiles = Array.isArray(message.files) && message.files.length > 0 ? message.files.filter((el) => el.content_type.includes('audio')) : [];
        const videos = Array.isArray(message.files) && message.files.length > 0 ? message.files.filter((el) => el.content_type.includes('video')) : [];
        if (message.role === 'system') {
            return null;
        }
        return (
            <Message
                key={index}
                model={{
                    message: message.content,
                    sender: message.role,
                    direction: message.role === 'assistant' ? 'incoming' : 'outgoing',
                    position: 0,
                }}
            >
                <Message.Header
                    sender={message.role === 'assistant' ? model?.name : message.role}
                    sentTime={typeof message.timestamp === 'number' ? moment.unix(message.timestamp).format('DD.MM.YYYY HH:mm') : ''}
                />
                {(images.length > 0 || otherFiles.length > 0 || audioFiles.length > 0 || videos.length > 0) && (
                    <Message.CustomContent>
                        <Space direction="vertical">
                            {images.length > 0 && (
                                <Image.PreviewGroup>
                                    {images.map((file) => (
                                        <Image
                                            data-file-id={file.id}
                                            className="image-file"
                                            src={`https://ai.retechcrm.ru/api/v1/files/${file.id}/content`}
                                            style={{ borderRadius: 10, width: 150 }}
                                            preview={{ src: `https://ai.retechcrm.ru/api/v1/files/${file.id}/content` }}
                                        />
                                    ))}
                                </Image.PreviewGroup>
                            )}
                            {otherFiles.length > 0 && otherFiles.map((file) => (
                                <a
                                    key={file.id}
                                    href={`https://ai.retechcrm.ru/api/v1/files/${file.id}/content`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="chat-link"
                                >
                                    <Space>
                                        <DownloadOutlined />
                                        <span>{file.name}</span>
                                    </Space>
                                </a>
                            ))}
                            {audioFiles.length > 0 && audioFiles.map((file) => (
                                <audio controls src={`https://ai.retechcrm.ru/api/v1/files/${file.id}/content`} style={{ borderRadius: 10, maxHeight: 500, maxWidth: 500 }}>
                                    <track default kind="captions" srcLang="ru" />
                                </audio>
                            ))}
                            {videos.length > 0 && videos.map((file) => (
                                <video controls src={`https://ai.retechcrm.ru/api/v1/files/${file.id}/content`} style={{ borderRadius: 10, maxHeight: 500, maxWidth: 500 }}>
                                    <track default kind="captions" srcLang="ru" />
                                </video>
                            ))}
                            <Message.TextContent>{message.content}</Message.TextContent>
                        </Space>
                    </Message.CustomContent>
                )}
            </Message>
        );
    };

    if (!show) {
        return null;
    }

    const selectedFilesImages = selectedFiles.filter((el) => el.meta.content_type.includes('image'));
    const selectedFilesOther = selectedFiles.filter((el) => !el.meta.content_type.includes('image'));

    return (
        <Scrollbars autoHide className="scrollable-content">
            <Space className="w-100" direction="vertical">
                <Card
                    className="page-object-horizontal-margin"
                    title={loading && typeof model === 'undefined' ? 'Чат с агентом' : `Чат с агентом ${model?.name}`}
                    loading={loading}
                >
                    <MainContainer>
                        <Space className="w-100" direction="vertical">
                            <ChatContainer>
                                <MessageList id="messages-list" style={{ height: selectedFiles.length > 0 ? 'calc(100vh - 332px)' : 'calc(100vh - 286px)' }} loading={loading} typingIndicator={waiting ? <TypingIndicator content={model?.name} /> : undefined}>
                                    {messages.map(renderMessage)}
                                </MessageList>
                            </ChatContainer>
                            <Space className="w-100" direction="vertical" style={{ borderTop: 'solid 1px #d1dbe3' }}>
                                <span />
                                {selectedFiles.length > 0 && (
                                    <div style={{
                                        display: 'flex', flexDirection: 'row', gap: 5, margin: '0px 10px 0px 10px',
                                    }}
                                    >
                                        {selectedFiles.length > 0 && (
                                            <>
                                                {selectedFilesImages.length > 0 && selectedFilesImages.map((file) => (
                                                    <div className="uploaded-file-container">
                                                        <Image
                                                            className="image-file"
                                                            key={file.id}
                                                            alt=""
                                                            data-file-id={file.id}
                                                            src={`https://ai.retechcrm.ru/api/v1/files/${file.id}/content`}
                                                            preview={{ src: `https://ai.retechcrm.ru/api/v1/files/${file.id}/content` }}
                                                            style={{
                                                                height: 40,
                                                                width: 40,
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                        <Button
                                                            className="uploaded-file-delete-btn"
                                                            icon={<CloseOutlined />}
                                                            type="default"
                                                            shape="circle"
                                                            size="small"
                                                            danger
                                                            onClick={() => onFileDeleteClick(file)}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                        {selectedFilesOther.length > 0 && (
                                            <>
                                                {selectedFilesOther.length > 0 && selectedFilesOther.map((file) => (
                                                    <div className="uploaded-file-container">
                                                        <Tooltip title={file.filename}>
                                                            <div
                                                                key={file.id}
                                                                style={{
                                                                    height: 40,
                                                                    width: 40,
                                                                    backgroundColor: '#c6e3fa',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    display: 'flex',
                                                                    fontSize: 20,
                                                                    color: '#000',
                                                                }}
                                                            >
                                                                <Space>
                                                                    <FileDoneOutlined />
                                                                </Space>
                                                            </div>
                                                        </Tooltip>
                                                        <Button
                                                            className="uploaded-file-delete-btn"
                                                            icon={<CloseOutlined />}
                                                            type="default"
                                                            shape="circle"
                                                            size="small"
                                                            danger
                                                            onClick={() => onFileDeleteClick(file)}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                )}
                                <Spin spinning={sending || uploading}>
                                    <MessageInput
                                        placeholder="Введите сообщение"
                                        attachButton
                                        onAttachClick={onAttachClick}
                                        onSend={onMessageSend}
                                        sendDisabled={sending || waiting}
                                        attachDisabled={uploading}
                                    />
                                </Spin>
                            </Space>
                            <span />
                        </Space>
                        <input
                            hidden
                            ref={inputRef}
                            multiple
                            type="file"
                            onChange={onFileSelect}
                        />
                    </MainContainer>
                </Card>
            </Space>
        </Scrollbars>
    );
};

export default AiAgentChatPage;
